import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/SubscriptionAppCard';
import { Button } from '../components/SubscriptionAppButton';
import { Input } from '../components/SubscriptionAppInput';
import { NavBar } from '../components/NavBar';
import { MobileUnsupported } from '../components/MobileUnsupported';
import { EditTagModal } from '../components/EditTagModal';
import { useTags } from '../contexts/TagsContext';
import type { TagData } from '../contexts/TagsContext';
import { Trash2, Edit2, Plus, X, Check, Sparkles } from 'lucide-react';
interface AutomationRule {
  id: string;
  event: string;
  eventValue?: string | number;
  condition?: {
    field: string;
    operator: string;
    value: string | number;
  };
  action: 'apply' | 'remove';
}
interface TagManagementPageProps {
  currentPage?: string;
  onNavigate?: (page: string) => void;
}
export function TagManagementPage({
  currentPage = 'Settings',
  onNavigate
}: TagManagementPageProps) {
  const navigate = useNavigate();
  // Get tags from shared context
  const {
    allTags,
    createTag,
    updateTag,
    deleteTag
  } = useTags();
  // State
  const [isCreating, setIsCreating] = useState(false);
  const [editingTagId, setEditingTagId] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTag, setSelectedTag] = useState<TagData | null>(null);
  const [newTagLabel, setNewTagLabel] = useState('');
  const [newTagColor, setNewTagColor] = useState<'blue' | 'green' | 'orange' | 'purple' | 'gray' | string>('blue');
  const [newCustomColor, setNewCustomColor] = useState('#0EA5E9');
  const [editTagLabel, setEditTagLabel] = useState('');
  const [editTagColor, setEditTagColor] = useState<'blue' | 'green' | 'orange' | 'purple' | 'gray' | string>('blue');
  const [editCustomColor, setEditCustomColor] = useState('#0EA5E9');
  // Color options
  const colorOptions: Array<{
    value: 'blue' | 'green' | 'orange' | 'purple' | 'gray';
    label: string;
    preview: string;
  }> = [{
    value: 'blue',
    label: 'Blue',
    preview: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]'
  }, {
    value: 'green',
    label: 'Green',
    preview: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]'
  }, {
    value: 'orange',
    label: 'Orange',
    preview: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]'
  }, {
    value: 'purple',
    label: 'Purple',
    preview: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]'
  }, {
    value: 'gray',
    label: 'Gray',
    preview: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
  }];
  // Get color classes for tag display
  const getColorClasses = (color: string) => {
    const colorMap = {
      blue: 'bg-[#DBEAFE] text-[#1E40AF] border-[#BFDBFE]',
      green: 'bg-[#D1FAE5] text-[#065F46] border-[#A7F3D0]',
      orange: 'bg-[#FED7AA] text-[#C2410C] border-[#FDBA74]',
      purple: 'bg-[#E9D5FF] text-[#6B21A8] border-[#D8B4FE]',
      gray: 'bg-[#F1F5F9] text-[#475569] border-[#CBD5E1]'
    };
    // If it's a custom color (starts with #), return empty string
    if (color.startsWith('#')) {
      return '';
    }
    return colorMap[color as keyof typeof colorMap] || colorMap.gray;
  };
  // Get inline styles for custom colors
  const getCustomColorStyles = (color: string) => {
    if (!color.startsWith('#')) return {};
    // Calculate lighter background and border colors
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };
    const rgb = hexToRgb(color);
    if (!rgb) return {};
    return {
      backgroundColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.15)`,
      color: color,
      borderColor: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.3)`
    };
  };
  // Handlers
  const handleCreateTag = () => {
    if (!newTagLabel.trim()) return;
    const newTag: TagData = {
      id: newTagLabel.toLowerCase().replace(/\s+/g, '-'),
      label: newTagLabel,
      color: newTagColor
    };
    createTag(newTag);
    setNewTagLabel('');
    setNewTagColor('blue');
    setIsCreating(false);
  };
  const handleStartEdit = (tag: TagData) => {
    setSelectedTag(tag);
    setEditModalOpen(true);
  };
  const handleSaveEditModal = (tagData: {
    label: string;
    color: string;
    rules: AutomationRule[];
  }) => {
    if (selectedTag) {
      updateTag(selectedTag.id, {
        label: tagData.label,
        color: tagData.color,
        rules: tagData.rules
      });
    }
    setEditModalOpen(false);
    setSelectedTag(null);
  };
  const handleDeleteFromModal = () => {
    if (selectedTag && confirm('Are you sure you want to delete this tag? It will be removed from all subscriptions and contacts.')) {
      deleteTag(selectedTag.id);
      setEditModalOpen(false);
      setSelectedTag(null);
    }
  };
  const handleStartEditInline = (tag: TagData) => {
    setEditingTagId(tag.id);
    setEditTagLabel(tag.label);
    setEditTagColor(tag.color);
    // If it's a custom color, also set the custom color state
    if (tag.color.startsWith('#')) {
      setEditCustomColor(tag.color);
    }
  };
  const handleSaveEdit = () => {
    if (!editTagLabel.trim() || !editingTagId) return;
    updateTag(editingTagId, {
      label: editTagLabel,
      color: editTagColor
    });
    setEditingTagId(null);
    setEditTagLabel('');
    setEditTagColor('blue');
  };
  const handleCancelEdit = () => {
    setEditingTagId(null);
    setEditTagLabel('');
    setEditTagColor('blue');
  };
  const handleDeleteTag = (tagId: string) => {
    if (confirm('Are you sure you want to delete this tag? It will be removed from all subscriptions and contacts.')) {
      deleteTag(tagId);
    }
  };
  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewTagLabel('');
    setNewTagColor('blue');
  };
  return <>
      {/* Mobile Unsupported Message */}
      <div className="block md:hidden" data-id="element-13632" data-mp="2cn9cl3ohw2mw">
        <MobileUnsupported data-id="element-13633" data-mp="27qiukat4qwyu" />
      </div>

      {/* Desktop Content */}
      <div className="hidden md:block bg-[#F1F5F9] min-h-full p-6" data-id="element-13634" data-mp="27sdg9rc86t3z">
        {/* NavBar */}
        <div className="mb-6" data-id="element-13635" data-mp="2ml7728wy6vny">
          <NavBar tabs={[]} title="Member Tags" subtitle="Create, edit, and organize tags for your subscriptions and contacts" data-id="element-13636" data-mp="2hogkr3ujv3u3" />
        </div>

        {/* Main Content */}
        <div className="space-y-6" data-id="element-13637" data-mp="1tw9r4cg7layd">
          <Card data-id="element-13638" data-mp="1p01eb5phhtud">
            {/* Subtitle and Create Button Row */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-13639" data-mp="1oxos2gcp0xbi">
              <p className="text-[14px] text-[#64748B]" data-id="element-13640" data-mp="1yxpck5matjha">
                Create, edit, and organize tags for your subscriptions and
                contacts
              </p>
              {!isCreating && <Button variant="primary" onClick={() => setIsCreating(true)} iconLeft={<Plus className="w-4 h-4" data-id="element-13642" data-mp="168idn5ixmrvi" />} data-id="element-13641" data-mp="1b5rzycb0ynf4">
                  Create Tag
                </Button>}
            </div>

            {/* Create New Tag Form */}
            {isCreating && <div className="mb-6 p-4 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg" data-id="element-13643" data-mp="3mndhecbs7062">
                <h3 className="text-[16px] font-semibold text-[#0F172A] mb-4" data-id="element-13644" data-mp="37ypjw0idkgdh">
                  Create New Tag
                </h3>
                <div className="space-y-4" data-id="element-13645" data-mp="3cxttgs97og4c">
                  <div data-id="element-13646" data-mp="4hh4dnb4dslx">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-13647" data-mp="4h7gq6djrxje">
                      Tag Name
                    </label>
                    <Input type="text" value={newTagLabel} onChange={e => setNewTagLabel(e.target.value)} placeholder="e.g., VIP Customer" autoFocus data-id="element-13648" data-mp="9ehbm1jph1em" />
                  </div>
                  <div data-id="element-13649" data-mp="3vm76lhms8o9l">
                    <label className="block text-[12px] font-medium text-[#475569] mb-2" data-id="element-13650" data-mp="3vpv0wj01xepd">
                      Color
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-id="element-13651" data-mp="2k8vajsx9l9xz">
                      {colorOptions.map(option => <button key={option.value} onClick={() => setNewTagColor(option.value)} className={`px-4 py-2 rounded-md border-2 transition-all ${newTagColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-13652" data-mp="3bx93td5tjfu1">
                          <span className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium border ${option.preview}`} data-id="element-13653" data-mp="2xx6syavxhmoa">
                            {option.label}
                          </span>
                        </button>)}
                    </div>

                    {/* Custom Color Picker */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-white border border-[#E2E8F0] rounded-md" data-id="element-13654" data-mp="3lt2ppn5fgloh">
                      <label className="flex items-center gap-2 cursor-pointer" data-id="element-13655" data-mp="i5agsgxbkcr4">
                        <input type="color" value={newCustomColor} onChange={e => {
                      setNewCustomColor(e.target.value);
                      setNewTagColor(e.target.value);
                    }} className="w-10 h-10 rounded border border-[#CBD5E1] cursor-pointer" data-id="element-13656" data-mp="i7divzbqxwc7" />
                        <span className="text-[12px] text-[#475569]" data-id="element-13657" data-mp="pp1w5yiai41a">
                          Custom Color
                        </span>
                      </label>
                      <div className="flex-1 w-full flex items-center gap-2" data-id="element-13658" data-mp="yqj7l3kr5hjn">
                        <input type="text" value={newTagColor.startsWith('#') ? newTagColor : newCustomColor} onChange={e => {
                      const value = e.target.value;
                      if (value.startsWith('#')) {
                        setNewCustomColor(value);
                        setNewTagColor(value);
                      }
                    }} placeholder="#0EA5E9" className="flex-1 px-3 py-2 text-[14px] border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20" data-id="element-13659" data-mp="13p2y7haajhic" />
                        {newTagColor.startsWith('#') && <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium border whitespace-nowrap" style={getCustomColorStyles(newTagColor)} data-id="element-13660" data-mp="15quwcmzn41o0">
                            Preview
                          </span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3" data-id="element-13661" data-mp="1kfrejcveuv2c">
                    <Button variant="primary" onClick={handleCreateTag} disabled={!newTagLabel.trim()} iconLeft={<Check className="w-4 h-4" data-id="element-13663" data-mp="1tfe4n2ioweg3" />} data-id="element-13662" data-mp="1oh2v6swb45ye">
                      Create Tag
                    </Button>
                    <Button variant="outline" onClick={handleCancelCreate} data-id="element-13664" data-mp="1ye7gyrm8pzeo">
                      Cancel
                    </Button>
                  </div>
                </div>
              </div>}

            {/* Tags List */}
            <div className="space-y-3" data-id="element-13665" data-mp="4216jv167mnf">
              <h3 className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-wider" data-id="element-13666" data-mp="432hlzi6s955">
                All Tags ({allTags.length})
              </h3>

              {allTags.length === 0 ? <div className="text-center py-12" data-id="element-13667" data-mp="2mc6xrdod2h8t">
                  <p className="text-[14px] text-[#94A3B8]" data-id="element-13668" data-mp="2hj43127ul1r1">
                    No tags yet. Create your first tag to get started.
                  </p>
                </div> : <div className="space-y-2" data-id="element-13669" data-mp="28inyrqja5ql4">
                  {allTags.map(tag => <div key={tag.id} className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 p-4 bg-white border border-[#E2E8F0] rounded-lg hover:border-[#CBD5E1] transition-colors" data-id="element-13670" data-mp="28k8uhjhbzfkk">
                      {editingTagId === tag.id ?
                // Edit Mode (Inline)
                <div className="flex-1 flex flex-col lg:flex-row items-start lg:items-center gap-4" data-id="element-13671" data-mp="2w6yt0auh0s0f">
                          <div className="flex-1 w-full" data-id="element-13672" data-mp="2r8nhzwsfwbgm">
                            <Input type="text" value={editTagLabel} onChange={e => setEditTagLabel(e.target.value)} placeholder="Tag name" data-id="element-13673" data-mp="1ktmmttb8nxb3" />
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-13674" data-mp="1fwcykgzl5rou">
                            {colorOptions.map(option => <button key={option.value} onClick={() => setEditTagColor(option.value)} className={`px-3 py-1.5 rounded-md border transition-all ${editTagColor === option.value ? 'border-[#0EA5E9] ring-2 ring-[#0EA5E9] ring-opacity-20' : 'border-[#E2E8F0] hover:border-[#CBD5E1]'}`} data-id="element-13675" data-mp="1h9cq66upgzo1">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${option.preview}`} data-id="element-13676" data-mp="1dbezezlae5ij">
                                  {option.label.charAt(0)}
                                </span>
                              </button>)}
                            <label className="cursor-pointer" data-id="element-13677" data-mp="plug2fl8168q">
                              <input type="color" value={editTagColor.startsWith('#') ? editTagColor : editCustomColor} onChange={e => {
                        setEditCustomColor(e.target.value);
                        setEditTagColor(e.target.value);
                      }} className="w-8 h-8 rounded border border-[#CBD5E1] cursor-pointer" title="Custom color" data-id="element-13678" data-mp="tktdzg8jc2qo" />
                            </label>
                          </div>
                          <div className="flex gap-2" data-id="element-13679" data-mp="3s78wdeunqplv">
                            <button onClick={handleSaveEdit} disabled={!editTagLabel.trim()} className="p-2 text-[#10B981] hover:bg-[#D1FAE5] rounded-md transition-colors disabled:opacity-50" data-id="element-13680" data-mp="axknkskazi6l">
                              <Check className="w-5 h-5" data-id="element-13681" data-mp="jxh2w3ksz4oz" />
                            </button>
                            <button onClick={handleCancelEdit} className="p-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-md transition-colors" data-id="element-13682" data-mp="ezfm5paunhgo">
                              <X className="w-5 h-5" data-id="element-13683" data-mp="tn94hhrbky2y" />
                            </button>
                          </div>
                        </div> :
                // View Mode
                <>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-1" data-id="element-13684" data-mp="12oqfwmts8blb">
                            <span className={`inline-flex items-center px-3 py-1 rounded text-[14px] font-medium border ${getColorClasses(tag.color)}`} style={tag.color.startsWith('#') ? getCustomColorStyles(tag.color) : undefined} data-id="element-13685" data-mp="yksh6d8dd2ss">
                              {tag.label}
                            </span>
                            <span className="text-[12px] text-[#94A3B8]" data-id="element-13686" data-mp="31xrqvngvd0q4">
                              ID: {tag.id}
                            </span>
                            {/* Automation Indicator */}
                            {tag.rules && tag.rules.length > 0 && <div className="relative group" data-id="element-13687" data-mp="2e6w2l0bmgnnm">
                                <button onClick={() => handleStartEdit(tag)} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-[#F3E8FF] hover:bg-[#E9D5FF] transition-colors" title={`${tag.rules.length} automation ${tag.rules.length === 1 ? 'rule' : 'rules'}`} data-id="element-13688" data-mp="2j4xnlqslyx1q">
                                  <Sparkles className="w-3.5 h-3.5 text-[#8B5CF6]" data-id="element-13689" data-mp="3um5q26jp2339" />
                                  <span className="text-[11px] font-medium text-[#7C3AED]" data-id="element-13690" data-mp="2epg6cqw6igu">
                                    {tag.rules.length}
                                  </span>
                                </button>
                                {/* Tooltip */}
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-[#1E293B] text-white text-[11px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10" data-id="element-13691" data-mp="1e4zec9epq65x">
                                  {tag.rules.length} automation{' '}
                                  {tag.rules.length === 1 ? 'rule' : 'rules'}
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1" data-id="element-13692" data-mp="v7y68fpv3pxr">
                                    <div className="border-4 border-transparent border-t-[#1E293B]" data-id="element-13693" data-mp="q9wmdskm3ml8"></div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                          <div className="flex items-center gap-2" data-id="element-13694" data-mp="33qy8vcxnyfyk">
                            <button onClick={() => handleStartEdit(tag)} className="p-2 text-[#0EA5E9] hover:bg-[#E0F2FE] rounded-md transition-colors" title="Edit tag" data-id="element-13695" data-mp="33olo6s0itrht">
                              <Edit2 className="w-4 h-4" data-id="element-13696" data-mp="2url1cpt3o1gh" />
                            </button>
                            <button onClick={() => handleDeleteTag(tag.id)} className="p-2 text-[#EF4444] hover:bg-[#FEE2E2] rounded-md transition-colors" title="Delete tag" data-id="element-13697" data-mp="2zpwbz2r7yfzr">
                              <Trash2 className="w-4 h-4" data-id="element-13698" data-mp="3rgkbybh5pfbu" />
                            </button>
                          </div>
                        </>}
                    </div>)}
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* Edit Tag Modal */}
      {selectedTag && <EditTagModal isOpen={editModalOpen} onClose={() => {
      setEditModalOpen(false);
      setSelectedTag(null);
    }} onSave={handleSaveEditModal} onDelete={handleDeleteFromModal} tag={selectedTag} data-id="element-13699" data-mp="2212y9osdm032" />}
    </>;
}